import { FC, useContext } from "react";
import { useAtomValue } from "jotai";
import { ModuleContext } from "@/lib/Context";
import { negativeState, positiveState } from "@/lib/store";
import { Tooltip, LinearProgress } from "@mui/material";

interface PropsTypes {
    max?: number;
    percentage: number;
    tooltip?: string;
    className?: string;
    gradientColor?: boolean;
    /** What the bar measures (a column name, say); it starts the bar's accessible name. */
    label?: string;
}

// Middle band of a gradient bar: neither good nor bad, so it is not a user colour.
const NEUTRAL_AMBER = "#f4bc1a";

const ProgressBar: FC<PropsTypes> = ({ max = 100, percentage, tooltip, className, gradientColor, label }) => {
    const { obfuscated } = useContext(ModuleContext);
    const positiveColor = useAtomValue(positiveState);
    const negativeColor = useAtomValue(negativeState);
    const maxValue = 100; // Material UI LinearProgress Max Value

    // The bar takes the user's chosen positive and negative colours, as prices do. The MUI
    // `color` prop below is kept only for the track (the unfilled part), which it tints.
    const muiColor = gradientColor ? (percentage < 40 ? "error" : percentage > 40 && percentage < 60 ? "inherit" : "primary") : percentage > 0 ? "success" : "error";
    // A progressbar needs an accessible name. A locked (obfuscated) cell names only what it measures.
    const hidden = obfuscated || Boolean(className);
    const shownValue = tooltip ?? (Number.isFinite(Number(percentage)) ? String(percentage) : "");
    const ariaLabel = hidden || !shownValue ? label || "Value" : `${label || "Value"}: ${shownValue}`;

    const barColor = gradientColor ? (percentage < 40 ? negativeColor : percentage > 40 && percentage < 60 ? NEUTRAL_AMBER : positiveColor) : percentage > 0 ? positiveColor : negativeColor;

    return (
        <>
            <Tooltip title={obfuscated || className ? undefined : tooltip} arrow>
                <LinearProgress
                    variant="determinate"
                    aria-label={ariaLabel}
                    value={className ? 0 : Math.min(maxValue, (Math.abs(percentage) * maxValue) / max)}
                    color={muiColor}
                    sx={{
                        width: "100%",
                        height: 8,
                        "& .MuiLinearProgress-bar": { backgroundColor: barColor },
                    }}
                    className={className}
                />
            </Tooltip>
        </>
    );
};

export default ProgressBar;
